import {
  ANALYSIS_FAMILIES,
  MEDICAL_COVERAGE_TYPES,
  MEDICAL_EXAM_CATEGORIES
} from '../../../constants/medicalExamCategories';

const TAB_ANALYSIS = MEDICAL_EXAM_CATEGORIES.ANALYSIS.id;
const TAB_RADIO = MEDICAL_EXAM_CATEGORIES.RADIOGRAPHY.id;
const TAB_ULTRA = MEDICAL_EXAM_CATEGORIES.ULTRASOUND.id;

export function coverageLabel(id) {
  return MEDICAL_COVERAGE_TYPES[id]?.label || id || '—';
}

export function familyLabel(id) {
  return ANALYSIS_FAMILIES[id]?.label || id || '';
}

/** Détails examen pour le catalogue (sans prise en charge — filtre séparé). */
export function examMetaLine(exam) {
  const parts = [];
  if (exam.category === TAB_ANALYSIS && exam.analysis_family) {
    parts.push(familyLabel(exam.analysis_family));
  }
  const meta = exam.metadata || {};
  if (exam.category === TAB_ANALYSIS && meta.sample_type) {
    parts.push(meta.sample_type);
  }
  if (exam.category === TAB_RADIO) {
    if (meta.body_region) parts.push(meta.body_region);
    if (meta.projection) parts.push(meta.projection);
  }
  if (exam.category === TAB_ULTRA) {
    if (meta.body_region) parts.push(meta.body_region);
    if (meta.exam_type) parts.push(meta.exam_type);
  }
  return parts.join(' · ');
}

export function mapOrderToLine(order) {
  return {
    catalog_exam_id: order.catalog_exam_id,
    exam_name: order.exam_name,
    category: String(order.category || '').toUpperCase(),
    coverage_type: order.coverage_type,
    analysis_family: order.analysis_family,
    metadata: order.metadata || {}
  };
}

/** Réponse API liste / sync des demandes d'examens. */
export function parseExamOrdersResponse(res) {
  if (!res?.success) {
    return {
      orders: [],
      clinical_information_analysis: '',
      clinical_information_radiography: '',
      clinical_information_ultrasound: ''
    };
  }
  const data = res.data;
  if (Array.isArray(data)) {
    return {
      orders: data.map(mapOrderToLine),
      clinical_information_analysis: '',
      clinical_information_radiography: '',
      clinical_information_ultrasound: ''
    };
  }
  const legacy = data?.clinical_information || '';
  return {
    orders: Array.isArray(data?.orders) ? data.orders.map(mapOrderToLine) : [],
    clinical_information_analysis:
      data?.clinical_information_analysis != null
        ? data.clinical_information_analysis
        : legacy,
    clinical_information_radiography: data?.clinical_information_radiography || '',
    clinical_information_ultrasound: data?.clinical_information_ultrasound || ''
  };
}

export function getClinicalInformationForCategory(parsed, category) {
  const id = String(category || '').toUpperCase();
  if (id === TAB_RADIO) {
    return parsed.clinical_information_radiography || '';
  }
  if (id === TAB_ULTRA) {
    return parsed.clinical_information_ultrasound || '';
  }
  return parsed.clinical_information_analysis || '';
}

export function groupExamOrdersByCategory(orders = []) {
  const norm = (c) => String(c || '').toUpperCase();
  return {
    analyses: orders.filter((o) => norm(o.category) === TAB_ANALYSIS),
    radiographies: orders.filter((o) => norm(o.category) === TAB_RADIO),
    echographies: orders.filter((o) => norm(o.category) === TAB_ULTRA)
  };
}

export function formatConsultationDate(consultation) {
  const raw = consultation?.consultation_date || consultation?.created_at;
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleString('fr-FR', {
      dateStyle: 'long',
      timeStyle: 'short'
    });
  } catch {
    return String(raw);
  }
}

export function formatPrintDate() {
  return new Date().toLocaleString('fr-FR', {
    dateStyle: 'long',
    timeStyle: 'short'
  });
}

export function getCategoryLabelPlural(category) {
  const config = MEDICAL_EXAM_CATEGORIES[String(category || '').toUpperCase()];
  return config?.labelPlural || 'Examens';
}

export function getClinicalPlaceholder(category) {
  const id = String(category || '').toUpperCase();
  if (id === TAB_RADIO) return 'Contexte clinique pour le bon de radiographies…';
  if (id === TAB_ULTRA) return 'Contexte clinique pour le bon d’échographie…';
  return 'Contexte clinique pour le bon d’analyses…';
}

export function getClinicalHint(category) {
  const id = String(category || '').toUpperCase();
  if (id === TAB_RADIO) {
    return 'Propre à cet onglet — affiché uniquement sur le bon de radiographies.';
  }
  if (id === TAB_ULTRA) {
    return 'Propre à cet onglet — affiché uniquement sur le bon d’échographie.';
  }
  return 'Propre à cet onglet — affiché uniquement sur le bon d’analyses.';
}

export function getViewPrintLabel(category) {
  return `les ${getCategoryLabelPlural(category).toLowerCase()}`;
}

export function getTabEmptyMessage(category, saved = false) {
  const label = getCategoryLabelPlural(category).toLowerCase();
  const singular = label.slice(0, -1) || 'examen';
  if (saved) {
    return `Aucune ${singular} enregistrée pour cette consultation.`;
  }
  return `Aucune ${singular} dans la demande — ajoutez des examens dans cet onglet.`;
}
